// Каркас приложения: шапка с состоянием сервера, выбор датасета и страницы.
// Маршруты — React Router (routes.tsx, PLAN.md B2a): страница — путь, выбранный
// датасет — `?dataset=` в адресе, чтобы ссылка открывала тот же экран.

import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router';
import { StoreProvider, useStore } from './data/store';
import { applyTheme, readStoredTheme, storeTheme, type ThemeChoice } from './lib/theme';
import { PAGES, pagePath } from './routes';
import { num } from './lib/format';
import { Workspace } from './pages/Workspace';

/** Параметр адреса, общий для всех страниц; остальные — свои у каждой страницы. */
export const DATASET_PARAM = 'dataset';

const THEME_OPTIONS: Array<{ id: ThemeChoice; label: string; title: string }> = [
  { id: 'light', label: 'свет', title: 'светлая тема' },
  { id: 'dark', label: 'тьма', title: 'тёмная тема' },
  { id: 'system', label: 'система', title: 'как в системе' },
];

function ThemeSwitch() {
  const [choice, setChoice] = useState<ThemeChoice>(readStoredTheme);

  useEffect(() => {
    applyTheme(choice);
    // Положение «как в системе» обязано слушать систему: иначе тема
    // переключится только при перезагрузке страницы.
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [choice]);

  const pick = useCallback((next: ThemeChoice) => {
    setChoice(next);
    storeTheme(next);
  }, []);

  return (
    <div className="theme-switch" role="group" aria-label="Тема оформления">
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          title={option.title}
          aria-pressed={choice === option.id}
          onClick={() => pick(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Синхронизация выбранного датасета с `?dataset=` в обе стороны. */
function useDatasetInUrl() {
  const store = useStore();
  const [params, setParams] = useSearchParams();
  const wanted = params.get(DATASET_PARAM);

  // Адрес → выбор: «назад/вперёд» браузера и вставленная ссылка.
  useEffect(() => {
    if (wanted && wanted !== store.dataset && store.datasets.some((d) => d.name === wanted)) {
      store.setDataset(wanted);
    }
    // Зависимости — адрес и список, не выбор: иначе выбор пользователя тут же откатывался бы к адресу.
  }, [wanted, store.datasets]);

  // Выбор → адрес. replace: смена датасета — не новая страница в истории.
  // Пишем, если выбор сменился сам (пользователь в селекте) или в адресе
  // датасета нет / он неизвестен. Если же разошлись из-за адреса («назад»),
  // это забота эффекта выше — перезаписать адрес старым выбором было бы ошибкой.
  const lastSelected = useRef(store.dataset);
  useEffect(() => {
    const selectionChanged = lastSelected.current !== store.dataset;
    lastSelected.current = store.dataset;
    if (store.dataset === null || store.dataset === wanted) return;
    const wantedKnown = wanted !== null && store.datasets.some((d) => d.name === wanted);
    if (!selectionChanged && wantedKnown) return;
    const selected = store.dataset;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(DATASET_PARAM, selected);
        return next;
      },
      { replace: true },
    );
  }, [store.dataset, store.datasets, wanted, setParams]);
}

interface AuthUser { email: string; role: 'admin' | 'maintainer' }

function Shell({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const store = useStore();
  const location = useLocation();
  useDatasetInUrl();

  // Переход между страницами уносит с собой только датасет: параметры
  // страницы (пара на «Сравнении», ползунки «Тюнинга») к другой не относятся.
  const keep = store.dataset ? `?${new URLSearchParams({ [DATASET_PARAM]: store.dataset })}` : '';

  const online = store.server?.online ?? false;

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <strong>СПУСК</strong>
          <span>балансовый плейграунд</span>
        </div>

        <nav className="nav">
          {PAGES.slice(0, 3).map((entry) => (
            <NavLink key={entry.id} to={{ pathname: pagePath(entry.id), search: keep }} end>
              {entry.title}
            </NavLink>
          ))}
          <details className="nav-more"><summary>Аналитика</summary><div>
            {PAGES.slice(3, -1).map((entry) => <NavLink key={entry.id} to={{ pathname: pagePath(entry.id), search: keep }} end>{entry.title}</NavLink>)}
          </div></details>
          <NavLink to="/settings" end>Настройки</NavLink>
        </nav>

        <div className="status">
          <label className="field">
            <select
              value={store.dataset ?? ''}
              onChange={(e) => store.setDataset(e.target.value)}
              disabled={store.datasets.length === 0}
            >
              {store.datasets.length === 0 && <option value="">датасетов нет</option>}
              {store.datasets.map((entry) => (
                <option key={entry.name} value={entry.name}>
                  {entry.name} · {num(entry.rows)} · {entry.configHash?.slice(0, 8) ?? '—'}
                </option>
              ))}
            </select>
          </label>

          <span title={store.server?.reason ?? 'сервер отвечает'}>
            <span className={online ? 'dot on' : 'dot off'} />{' '}
            {store.server === null ? 'проверка…' : online ? 'сервер 5177' : 'без сервера'}
          </span>

          <button onClick={store.recheckServer}>проверить</button>

          <ThemeSwitch />
          <span className="user-chip" title={user.email}>{user.email}</span>
          <button onClick={onLogout}>Выйти</button>
        </div>
      </div>

      {!online && store.server !== null && (
        <div className="banner warn">
          <span className="mono warn">
            Сервер на 127.0.0.1:5177 не отвечает ({store.server.reason}). Все числа ниже — из
            запечённых образцов настоящих прогонов; пересчёт ползунками недоступен.
          </span>
        </div>
      )}

      {store.error && (
        <div className="banner bad">
          <span className="mono bad">{store.error}</span>
        </div>
      )}

      {store.loading && store.active.length === 0 ? (
        <div className="page">
          <div className="empty">загрузка выборки…</div>
        </div>
      ) : (
        // key по пути: страница монтируется заново, как при прежней смене хэша.
        <Outlet key={location.pathname} />
      )}
    </div>
  );
}

/** Корневой маршрут: хранилище живёт внутри роутера, чтобы видеть адрес. */
export function Layout() {
  const [params] = useSearchParams();
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    fetch('/api/auth/me').then(async (response) => response.ok ? (await response.json()).user as AuthUser : null)
      .then((value) => { if (alive) setUser(value); }).catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  const logout = () => { void fetch('/api/auth/logout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).finally(() => setUser(null)); };
  if (user === undefined) return <div className="login-loading">Проверяю вход…</div>;
  if (user === null) return <Login onLogin={setUser} />;
  if (user.role === 'maintainer') return <div className="maintainer-app"><div className="maintainer-bar"><strong>СПУСК <span>Студия баланса</span></strong>
    <div><span>{user.email}</span><button onClick={logout}>Выйти</button></div></div><Workspace email={user.email} /></div>;
  return (
    <StoreProvider preferredDataset={params.get(DATASET_PARAM)}>
      <Shell user={user} onLogout={logout} />
    </StoreProvider>
  );
}

function Login({ onLogin }: { onLogin: (user: AuthUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      const response = await fetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }) });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Не удалось войти.');
      onLogin(body.user as AuthUser);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Не удалось войти.'); }
    finally { setBusy(false); }
  };
  return <div className="login-page"><div className="login-identity"><div className="login-mark">С</div><span>СПУСК / БАЛАНС</span>
    <h1>Собирайте баланс<br />в одной точке.</h1><p>Понятные шансы появления предметов, комнат и лута — с моментальным просмотром изменений.</p></div>
    <form className="login-card" onSubmit={submit}><div className="eyebrow">ДОСТУП К ПЛЕЙГРАУНДУ</div><h2>Вход в студию</h2><p>Введите рабочий email и пароль.</p>
      <label>Email<input type="email" required autoComplete="username" placeholder="name@example.com" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      <label>Пароль<input type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
      {error && <div className="login-error" role="alert">{error}</div>}
      <button className="primary-button" disabled={busy}>{busy ? 'Проверяю…' : 'Войти'}</button></form></div>;
}
